import { useEffect, useState } from 'react';
import { useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { Link, getRouteApi } from '@tanstack/react-router';
import {
  Plus,
  Lock,
  PlayCircle,
  CheckCircle2,
  FileEdit,
  RefreshCw,
  Building2,
  User,
  Calendar,
  ListOrdered,
  Search,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { Pagination } from '@/components/ui/pagination';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import { playbooksService, type PlaybookListItem } from '../services/playbooks.service';
import {
  STATUS_LABELS,
  STATUS_ACCENT_CLASS,
  STATUS_PROGRESS_CLASS,
  getPlaybookProgress,
  getProgressLabel,
  statusBadgeClass,
} from '../utils/playbook-status';
import { usePermissions } from '@/common/hooks/usePermissions';
import { useDebouncedValue } from '@/common/hooks/useDebouncedValue';
import { Permission } from '@/common/enums/permissions.enum';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import type { PlaybookStatus } from '../services/playbooks.service';

const PAGE_SIZE = 6;
const SEARCH_DEBOUNCE_MS = 500;

export type PlaybookStatusTab = 'all' | PlaybookStatus;

// eslint-disable-next-line react-refresh/only-export-components -- constante usada pela rota de playbooks
export const PLAYBOOK_STATUS_TAB_VALUES: PlaybookStatusTab[] = [
  'all',
  'in_progress',
  'draft',
  'completed',
  'cancelled',
];

const STATUS_TABS: Array<{
  value: PlaybookStatusTab;
  label: string;
  countKey: 'total' | PlaybookStatus;
}> = [
  { value: 'in_progress', label: 'Em execução', countKey: 'in_progress' },
  { value: 'all', label: 'Todos', countKey: 'total' },
  { value: 'draft', label: 'Rascunhos', countKey: 'draft' },
  { value: 'completed', label: 'Concluídos', countKey: 'completed' },
  { value: 'cancelled', label: 'Cancelados', countKey: 'cancelled' },
];

const playbooksRouteApi = getRouteApi('/_authenticated/playbooks/');

function PlaybookCard({ pb }: { pb: PlaybookListItem }) {
  const status = pb.status as PlaybookStatus;
  const progress = getPlaybookProgress(pb.stepsTotal, pb.stepsCompleted);
  const hasSteps = pb.stepsTotal > 0;

  return (
    <Card
      className={cn(
        'group overflow-hidden border-border/60 shadow-sm',
        'hover:shadow-md hover:border-border transition-all',
      )}
    >
      <div className="flex items-stretch">
        <div className={cn('w-1 shrink-0', STATUS_ACCENT_CLASS[status] ?? 'bg-muted')} />

        <div className="flex-1 min-w-0 p-4 space-y-3">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 space-y-1">
              <Link
                to="/playbooks/$id"
                params={{ id: pb.id }}
                className="font-semibold text-base leading-snug hover:text-primary transition-colors line-clamp-2"
              >
                {pb.title}
              </Link>
              {pb.clientName && (
                <p className="text-xs text-muted-foreground flex items-center gap-1 truncate">
                  <Building2 className="h-3 w-3 shrink-0" />
                  {pb.clientName}
                </p>
              )}
            </div>

            <div className="flex flex-col items-end gap-1 shrink-0">
              <div className="flex items-center gap-1">
                {pb.isPrivate && <Lock className="h-3.5 w-3.5 text-muted-foreground" />}
                {pb.requireSequentialSteps && (
                  <ListOrdered className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400" />
                )}
              </div>
              <Badge className={statusBadgeClass(status)}>
                {STATUS_LABELS[status] ?? pb.status}
              </Badge>
            </div>
          </div>

          {hasSteps ? (
            <div className="space-y-1.5">
              <div className="flex justify-between text-xs">
                <span className="text-muted-foreground">
                  {getProgressLabel(status, pb.stepsTotal, pb.stepsCompleted)}
                </span>
                <span
                  className={cn(
                    'font-medium tabular-nums',
                    status === 'completed' && 'text-emerald-600 dark:text-emerald-400',
                    status === 'in_progress' && 'text-blue-600 dark:text-blue-400',
                    status === 'cancelled' && 'text-red-600 dark:text-red-400',
                  )}
                >
                  {progress}%
                </span>
              </div>
              <Progress
                value={progress}
                className={cn('h-2', STATUS_PROGRESS_CLASS[status])}
              />
            </div>
          ) : (
            <p className="text-xs text-muted-foreground italic">Nenhuma etapa cadastrada</p>
          )}

          <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground pt-1 border-t border-border/40">
            <span className="flex items-center gap-1 truncate min-w-0">
              <User className="h-3 w-3 shrink-0" />
              <span className="truncate">{pb.creatorName ?? '—'}</span>
            </span>
            <span className="flex items-center gap-1 shrink-0">
              <Calendar className="h-3 w-3" />
              {format(new Date(pb.updatedAt), 'd MMM yyyy', { locale: ptBR })}
            </span>
          </div>

          <Button
            variant={status === 'in_progress' ? 'default' : 'outline'}
            size="sm"
            className="w-full"
            asChild
          >
            <Link to="/playbooks/$id" params={{ id: pb.id }}>
              {status === 'draft' ? (
                <>
                  <FileEdit className="h-3.5 w-3.5 mr-1.5" />
                  Editar rascunho
                </>
              ) : status === 'in_progress' ? (
                <>
                  <PlayCircle className="h-3.5 w-3.5 mr-1.5" />
                  Continuar execução
                </>
              ) : (
                <>
                  <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />
                  Ver detalhes
                </>
              )}
            </Link>
          </Button>
        </div>
      </div>
    </Card>
  );
}

export function PlaybooksBoard() {
  const queryClient = useQueryClient();
  const { hasPermission } = usePermissions();
  const navigate = playbooksRouteApi.useNavigate();
  const { status: statusFilter } = playbooksRouteApi.useSearch();
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search.trim(), SEARCH_DEBOUNCE_MS);
  const [page, setPage] = useState(1);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const isSearchPending = search.trim() !== debouncedSearch;

  const setStatusFilter = (value: PlaybookStatusTab) => {
    void navigate({
      search: (prev) => ({ ...prev, status: value }),
    });
  };

  useEffect(() => {
    setPage(1);
  }, [statusFilter, debouncedSearch]);

  const { data: listPage, isLoading, isFetching, isPlaceholderData } = useQuery({
    queryKey: ['playbooks-list', statusFilter, debouncedSearch, page],
    queryFn: () =>
      playbooksService.list({
        ...(statusFilter !== 'all' ? { status: statusFilter } : {}),
        ...(debouncedSearch ? { search: debouncedSearch } : {}),
        page,
        pageSize: PAGE_SIZE,
      }),
    staleTime: Infinity,
    placeholderData: keepPreviousData,
  });

  const playbooks = listPage?.data ?? [];
  const total = listPage?.total ?? 0;
  const summary = listPage?.summary;

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await queryClient.invalidateQueries({ queryKey: ['playbooks-list'] });
      await queryClient.refetchQueries({
        queryKey: ['playbooks-list', statusFilter, debouncedSearch, page],
      });
    } finally {
      setIsRefreshing(false);
    }
  };

  const activeTabLabel =
    STATUS_TABS.find((tab) => tab.value === statusFilter)?.label ?? 'Playbooks';

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Playbooks</h1>
          <p className="text-muted-foreground text-sm">
            Processos operacionais com checklists e histórico
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button
            variant="outline"
            size="sm"
            onClick={() => void handleRefresh()}
            disabled={isRefreshing || isLoading || isFetching}
          >
            <RefreshCw
              className={cn('h-4 w-4 mr-2', (isRefreshing || isFetching) && 'animate-spin')}
            />
            Atualizar
          </Button>
          {hasPermission(Permission.PLAYBOOKS_CREATE) && (
            <Button asChild>
              <Link to="/playbooks/new">
                <Plus className="h-4 w-4 mr-2" />
                Novo playbook
              </Link>
            </Button>
          )}
        </div>
      </div>

      <Tabs
        value={statusFilter}
        onValueChange={(value) => setStatusFilter(value as PlaybookStatusTab)}
        className="space-y-4"
      >
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <TabsList className="inline-flex h-auto w-full lg:w-auto flex-wrap justify-start gap-1 p-1">
            {STATUS_TABS.map((tab) => {
              const count = summary?.[tab.countKey] ?? 0;
              return (
                <TabsTrigger
                  key={tab.value}
                  value={tab.value}
                  className="gap-2 data-[state=active]:shadow-sm"
                >
                  {tab.label}
                  {!isLoading && count > 0 && (
                    <Badge
                      variant="secondary"
                      className="h-5 min-w-5 px-1.5 text-xs font-normal pointer-events-none"
                    >
                      {count}
                    </Badge>
                  )}
                </TabsTrigger>
              );
            })}
          </TabsList>

          <div className="relative w-full lg:max-w-sm shrink-0">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Pesquisar por nome do playbook..."
              className="pl-9 pr-24"
            />
            {(isSearchPending || (isFetching && !isLoading)) && (
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                {isSearchPending ? 'Aguardando...' : 'Buscando...'}
              </span>
            )}
          </div>
        </div>

        {isLoading ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <Skeleton className="h-48 w-full" />
            <Skeleton className="h-48 w-full" />
            <Skeleton className="h-48 w-full" />
          </div>
        ) : playbooks.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="py-16 text-center">
              <CheckCircle2 className="h-10 w-10 mx-auto text-muted-foreground/40 mb-3" />
              <p className="text-muted-foreground font-medium">Nenhum playbook encontrado</p>
              <p className="text-sm text-muted-foreground mt-1">
                {debouncedSearch
                  ? `Nenhum resultado em “${activeTabLabel}” para “${debouncedSearch}”.`
                  : `Nenhum playbook em “${activeTabLabel}”.`}
              </p>
            </CardContent>
          </Card>
        ) : (
          <>
            <p className="text-sm text-muted-foreground">
              {PAGE_SIZE} por página · {activeTabLabel.toLowerCase()}
              {debouncedSearch ? ` · busca: “${debouncedSearch}”` : ''}
              {debouncedSearch || total > PAGE_SIZE
                ? ` · ${total} encontrado(s)`
                : ''}
            </p>
            <div
              className={cn(
                'grid gap-4 sm:grid-cols-2 lg:grid-cols-3 transition-opacity',
                isPlaceholderData && isFetching && 'opacity-60',
              )}
            >
              {playbooks.map((pb) => (
                <PlaybookCard key={pb.id} pb={pb} />
              ))}
            </div>
            <Pagination
              total={total}
              page={page}
              limit={PAGE_SIZE}
              onPageChange={setPage}
              className="pt-2"
            />
          </>
        )}
      </Tabs>
    </div>
  );
}
